(function (root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.MDProductMediaGroups = api;
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  const metalCodes = ['WG', 'YG', 'RG', 'PT', 'PL', 'TT'];
  const galleryStates = new WeakMap();
  const browserRoot = typeof window !== 'undefined' ? window : globalThis;

  function normalizeCode(value) {
    return String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function normalizeMetalColor(value) {
    const normalized = normalizeCode(value);

    if (!normalized) return '';
    if (/(^|-)WG($|-)/.test(normalized) || normalized.includes('WHITE-GOLD')) return 'WG';
    if (/(^|-)YG($|-)/.test(normalized) || normalized.includes('YELLOW-GOLD')) return 'YG';
    if (/(^|-)RG($|-)/.test(normalized) || normalized.includes('ROSE-GOLD')) return 'RG';
    if (/(^|-)(PT|PL)($|-)/.test(normalized) || normalized.includes('PLATINUM')) return 'PT';
    if (/(^|-)TT($|-)/.test(normalized) || normalized.includes('TWO-TONE')) return 'TT';

    return normalized;
  }

  function normalizeShape(value) {
    return normalizeCode(value);
  }

  function parseVisualSku(sku) {
    const tokens = normalizeCode(sku).split('-').filter(Boolean);
    const metalIndex = tokens.findIndex((token) => metalCodes.includes(token));

    if (metalIndex === -1) {
      return { metalColor: '', diamondShape: '' };
    }

    return {
      metalColor: normalizeMetalColor(tokens[metalIndex]),
      diamondShape: tokens[metalIndex + 1] || ''
    };
  }

  function unique(values) {
    return values.filter((value, index) => value && values.indexOf(value) === index);
  }

  function findMatchingGroup(groups, context) {
    const skuVisual = parseVisualSku(context && context.sku);
    const metalCandidates = unique([
      normalizeMetalColor(context && context.selectedMetal),
      skuVisual.metalColor
    ]);
    const shapeCandidates = unique([
      normalizeShape(context && context.selectedShape),
      skuVisual.diamondShape
    ]);

    const normalizedGroups = (groups || []).map((group) => ({
      ...group,
      normalizedMetal: normalizeMetalColor(group.metalColor),
      normalizedShape: normalizeShape(group.diamondShape)
    }));

    for (const metalColor of metalCandidates) {
      const metalGroups = normalizedGroups.filter((group) => group.normalizedMetal === metalColor);

      for (const diamondShape of shapeCandidates) {
        const exactGroup = metalGroups.find((group) => group.normalizedShape === diamondShape);
        if (exactGroup) return exactGroup;
      }

      if (metalGroups.length === 1) return metalGroups[0];

      const availableShapes = unique(metalGroups.map((group) => group.normalizedShape));
      if (availableShapes.length === 1 && metalGroups.length) return metalGroups[0];
    }

    return null;
  }

  function buildOptionValuesUrl(productUrl, optionValueIds, sectionId, origin) {
    const baseOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : 'https://example.com');
    const url = new URL(productUrl, baseOrigin);
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', optionValueIds.join(','));
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function collectOptionValueIds(picker) {
    return Array.from(picker.querySelectorAll('select, input[type="radio"]:checked'))
      .map((control) => {
        if (control.tagName === 'SELECT') {
          return control.options[control.selectedIndex];
        }

        return control;
      })
      .map((control) => control && control.dataset.optionValueId)
      .filter(Boolean);
  }

  function getSelectedValue(picker, selector) {
    const wrapper = picker.querySelector(selector);
    if (!wrapper) return '';

    const select = wrapper.querySelector('select');
    if (select) return select.value;

    const checkedInput = wrapper.querySelector('input[type="radio"]:checked');
    return checkedInput ? checkedInput.value : '';
  }

  function cleanSlide(slide) {
    const clone = slide.cloneNode(true);
    clone.classList.remove('slick-slide', 'slick-active', 'slick-current', 'slick-center', 'slick-visible');
    clone.removeAttribute('aria-hidden');
    clone.removeAttribute('data-slick-index');
    clone.removeAttribute('style');
    return clone.outerHTML;
  }

  function captureSlides(slider) {
    if (!slider) return [];

    const slides = slider.classList.contains('slick-initialized')
      ? slider.querySelectorAll('.slick-slide:not(.slick-cloned)')
      : slider.children;

    return Array.from(slides).map(cleanSlide);
  }

  function getGalleryState(photos) {
    if (!galleryStates.has(photos)) {
      const featured = photos.querySelector('.proFeaturedImage');
      const thumbnails = photos.querySelector('#productThumbs .thumblist');
      galleryStates.set(photos, {
        currentKey: 'default',
        defaultFeatured: captureSlides(featured),
        defaultThumbnails: captureSlides(thumbnails)
      });
    }

    return galleryStates.get(photos);
  }

  function replaceSlides(slider, slideHtml) {
    if (!slider) return;

    slider.querySelectorAll('video').forEach((video) => video.pause());

    if (browserRoot.jQuery && browserRoot.jQuery.fn && browserRoot.jQuery.fn.slick && slider.classList.contains('slick-initialized')) {
      const $slider = browserRoot.jQuery(slider);
      $slider.slick('slickRemove', null, null, true);
      slideHtml.forEach((html) => $slider.slick('slickAdd', html));
      $slider.slick('slickGoTo', 0, true);
      $slider.slick('setPosition');
      return;
    }

    slider.innerHTML = slideHtml.join('');
  }

  function templateSlides(template, selector) {
    const container = template.content.querySelector(selector);
    return container ? Array.from(container.children).map((slide) => slide.outerHTML) : [];
  }

  function readGroups(picker) {
    const productSection = picker.closest('.product-template__container');
    if (!productSection) return [];

    return Array.from(productSection.querySelectorAll('template[data-md-media-group-template]')).map((template) => ({
      label: template.dataset.label || '',
      metalColor: template.dataset.metalColor || '',
      diamondShape: template.dataset.diamondShape || '',
      template
    }));
  }

  function update(picker, variant) {
    if (!picker) return false;

    const groups = readGroups(picker);
    if (!groups.length) return false;

    const productSection = picker.closest('.product-template__container');
    const photos = productSection && productSection.querySelector('.product-single__photos');
    if (!photos) return false;

    const state = getGalleryState(photos);
    const group = findMatchingGroup(groups, {
      sku: variant && variant.sku,
      selectedMetal: getSelectedValue(picker, '.md-product-option--metal'),
      selectedShape: getSelectedValue(picker, '.md-product-option--diamond-shape')
    });
    const nextKey = group ? `group:${group.label || `${group.metalColor}-${group.diamondShape}`}` : 'default';

    if (state.currentKey === nextKey) return Boolean(group);

    const featured = photos.querySelector('.proFeaturedImage');
    const thumbnails = photos.querySelector('#productThumbs .thumblist');
    const featuredSlides = group
      ? templateSlides(group.template, '[data-md-featured-items]')
      : state.defaultFeatured;
    const thumbnailSlides = group
      ? templateSlides(group.template, '[data-md-thumbnail-items]')
      : state.defaultThumbnails;

    if (!featuredSlides.length) return false;

    replaceSlides(featured, featuredSlides);
    replaceSlides(thumbnails, thumbnailSlides);
    state.currentKey = nextKey;

    if (thumbnails) {
      thumbnails.querySelectorAll('.thumbItem').forEach((thumbnail, index) => {
        thumbnail.classList.toggle('active', index === 0);
      });
    }

    const firstVideo = featured && featured.querySelector('.slick-current video, .item:first-child video');
    if (firstVideo) {
      firstVideo.muted = true;
      const playPromise = firstVideo.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    }

    return Boolean(group);
  }

  return {
    buildOptionValuesUrl,
    collectOptionValueIds,
    findMatchingGroup,
    normalizeMetalColor,
    normalizeShape,
    parseVisualSku,
    update
  };
});
